/**
 * AgentEcosystem — Admin
 * Vue d'ensemble visuelle de l'écosystème agents IA de SuiviPlus.
 * Inclut le schéma d'architecture Nova + les cartes agents.
 */

import React, { useState } from 'react';
import { Sparkles, Bot, Network, ChevronRight, Layers, Lock, Globe, FileText, Brain, Zap, ArrowDown, Radar } from 'lucide-react';

// ─── Données agents ────────────────────────────────────────────────────────────

const CURRENT_AGENTS = [
  {
    id: 'cygnus',
    name: 'Cygnus',
    emoji: '🌟',
    tagline: 'Support application',
    color: 'indigo',
    icon: Sparkles,
    fab: 'Via Nova (header)',
    access: 'Tous les utilisateurs',
    features: [
      { label: 'Chat conversationnel',    status: 'done' },
      { label: 'RAG Documentation .md',   status: 'done' },
      { label: 'RAG Documents PDF',        status: 'done' },
      { label: 'Mémoire persistante',      status: 'done' },
      { label: 'Personnalisation (ton…)',   status: 'done' },
      { label: 'Liens navigation modules', status: 'done' },
      { label: 'Context runtime',          status: 'done' },
      { label: 'Profil mémoire par user',  status: 'backlog' },
    ],
    description: 'Guide les utilisateurs dans l\'application : navigation, fonctionnalités, procédures et résolution de problèmes.',
  },
  {
    id: 'orion',
    name: 'Orion',
    emoji: '🤖',
    tagline: 'Ressources humaines',
    color: 'teal',
    icon: Bot,
    fab: 'Via Nova (header)',
    access: 'Admins uniquement',
    features: [
      { label: 'Chat conversationnel',    status: 'done' },
      { label: 'RAG Documents PDF',        status: 'done' },
      { label: 'Mémoire persistante',      status: 'done' },
      { label: 'Personnalisation (ton…)',   status: 'done' },
      { label: 'Analyse CV candidats',     status: 'done' },
      { label: 'Context runtime',          status: 'done' },
      { label: 'Profil mémoire par user',  status: 'backlog' },
    ],
    description: 'Assistant RH pour les micro-crèches : contrats, charges sociales, absences, recrutement et documents RH.',
  },
  {
    id: 'lyra',
    name: 'Lyra',
    emoji: '📋',
    tagline: 'Protocoles crèche',
    color: 'amber',
    icon: FileText,
    fab: 'Via Nova (header)',
    access: 'Tous les utilisateurs',
    features: [
      { label: 'Chat conversationnel',    status: 'done' },
      { label: 'RAG Documents PDF',        status: 'done' },
      { label: 'Mémoire persistante',      status: 'done' },
      { label: 'Personnalisation (ton…)',   status: 'done' },
      { label: 'Context runtime',          status: 'done' },
      { label: 'Validation Drive (N8N)',   status: 'done' },
      { label: 'Profil mémoire par user',  status: 'backlog' },
    ],
    description: 'Gestion des protocoles réglementaires : HACCP, hygiène alimentaire, sécurité des locaux, procédures sanitaires.',
  },
  {
    id: 'vega',
    name: 'VEGA',
    emoji: '📡',
    tagline: 'Agent proactif',
    color: 'emerald',
    icon: Radar,
    fab: 'Header (Radar)',
    access: 'Tous les utilisateurs',
    features: [
      { label: 'Chat conversationnel LLM', status: 'done' },
      { label: '8 analyseurs CRON',        status: 'done' },
      { label: 'Enrichissement LLM',       status: 'done' },
      { label: 'Mémoire persistante',      status: 'done' },
      { label: 'Personnalisation (ton...)', status: 'done' },
      { label: 'Seuils configurables',     status: 'done' },
      { label: 'Connaissance des agents',  status: 'done' },
      { label: 'CRON configurable',        status: 'done' },
      { label: 'Apprentissage habitudes',  status: 'backlog' },
    ],
    description: 'Agent Jarvis proactif : surveille stocks, traçabilité, maintenance, RH, budget, trésorerie, tâches et crèche. Détecte les risques et recommande des actions.',
  },
];

const ROADMAP_AGENTS = [
  {
    id: 'nova-llm',
    name: 'Nova Phase 2',
    emoji: '🔮',
    tagline: 'Routing LLM',
    color: 'purple',
    description: 'Remplacement du routing rules-based par un routing LLM (Haiku) pour une détection d\'intention plus fine.',
    eta: 'Backlog',
  },
];

// ─── Helpers visuels ───────────────────────────────────────────────────────────

const STATUS_BADGE = {
  done:    { cls: 'bg-green-100 text-green-700',  label: '✓' },
  wip:     { cls: 'bg-amber-100 text-amber-700',  label: '⏳' },
  backlog: { cls: 'bg-gray-100 text-gray-500',    label: '○' },
};

const COLOR_MAP = {
  indigo: { card: 'border-indigo-200 bg-indigo-50/40', badge: 'bg-indigo-100 text-indigo-700', icon: 'text-indigo-500', ring: 'ring-indigo-200', dot: 'bg-indigo-500', schema: 'border-indigo-300 bg-indigo-50 text-indigo-700' },
  teal:   { card: 'border-teal-200 bg-teal-50/40',     badge: 'bg-teal-100 text-teal-700',     icon: 'text-teal-500',   ring: 'ring-teal-200',   dot: 'bg-teal-500',   schema: 'border-teal-300 bg-teal-50 text-teal-700' },
  amber:  { card: 'border-amber-200 bg-amber-50/40',   badge: 'bg-amber-100 text-amber-700',   icon: 'text-amber-500',  ring: 'ring-amber-200',  dot: 'bg-amber-400',  schema: 'border-amber-300 bg-amber-50 text-amber-700' },
  purple:  { card: 'border-purple-200 bg-purple-50/40',   badge: 'bg-purple-100 text-purple-700',   icon: 'text-purple-500',  ring: 'ring-purple-200',  dot: 'bg-purple-500',  schema: 'border-purple-400 bg-purple-50 text-purple-800' },
  emerald: { card: 'border-emerald-200 bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700', icon: 'text-emerald-500', ring: 'ring-emerald-200', dot: 'bg-emerald-500', schema: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
};

// ─── Schéma d'architecture ─────────────────────────────────────────────────────

const ArchitectureSchema = () => (
  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">Architecture</div>

    {/* Nova — niveau 1 */}
    <div className="flex justify-center mb-1">
      <div className="border-2 border-purple-400 bg-purple-50 rounded-xl px-6 py-3 text-center shadow-sm">
        <div className="flex items-center gap-2 justify-center">
          <Network size={15} className="text-purple-600" />
          <span className="font-bold text-purple-800 text-sm">NOVA</span>
          <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">Header</span>
        </div>
        <div className="text-xs text-purple-600 mt-0.5">Orchestrateur — routing rules-based</div>
      </div>
    </div>

    {/* Lignes de connexion */}
    <div className="flex justify-center items-center gap-0 mb-1">
      <div className="flex flex-col items-center">
        <div className="w-px h-4 bg-gray-300" />
        <div className="w-64 sm:w-96 h-px bg-gray-300 relative">
          {/* 4 flèches vers le bas */}
          <div className="absolute left-0 -bottom-1 w-px h-4 bg-gray-300" style={{ transform: 'translateX(-0.5px)' }} />
          <div className="absolute left-1/3 -bottom-1 w-px h-4 bg-gray-300" style={{ transform: 'translateX(-0.5px)' }} />
          <div className="absolute left-2/3 -bottom-1 w-px h-4 bg-gray-300" style={{ transform: 'translateX(-0.5px)' }} />
          <div className="absolute right-0 -bottom-1 w-px h-4 bg-gray-300" style={{ transform: 'translateX(0.5px)' }} />
        </div>
      </div>
    </div>

    {/* Flèches descendantes + labels agents */}
    <div className="flex justify-center gap-2 sm:gap-4 mt-3 mb-1">
      {/* Cygnus */}
      <div className="flex flex-col items-center gap-1">
        <ArrowDown size={12} className="text-indigo-400" />
        <div className="border-2 border-indigo-300 bg-indigo-50 rounded-xl px-2.5 py-2.5 text-center w-20 sm:w-24">
          <div className="text-base mb-0.5">🌟</div>
          <div className="font-bold text-indigo-700 text-xs">Cygnus</div>
          <div className="text-xs text-indigo-500 leading-tight">Support</div>
        </div>
      </div>

      {/* Orion */}
      <div className="flex flex-col items-center gap-1">
        <ArrowDown size={12} className="text-teal-400" />
        <div className="border-2 border-teal-300 bg-teal-50 rounded-xl px-2.5 py-2.5 text-center w-20 sm:w-24">
          <div className="text-base mb-0.5">🤖</div>
          <div className="font-bold text-teal-700 text-xs">Orion</div>
          <div className="text-xs text-teal-500 leading-tight">RH</div>
        </div>
      </div>

      {/* Lyra */}
      <div className="flex flex-col items-center gap-1">
        <ArrowDown size={12} className="text-amber-400" />
        <div className="border-2 border-amber-300 bg-amber-50 rounded-xl px-2.5 py-2.5 text-center w-20 sm:w-24">
          <div className="text-base mb-0.5">📋</div>
          <div className="font-bold text-amber-700 text-xs">Lyra</div>
          <div className="text-xs text-amber-500 leading-tight">Protocoles</div>
        </div>
      </div>

      {/* VEGA */}
      <div className="flex flex-col items-center gap-1">
        <ArrowDown size={12} className="text-emerald-400" />
        <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl px-2.5 py-2.5 text-center w-20 sm:w-24">
          <div className="text-base mb-0.5">📡</div>
          <div className="font-bold text-emerald-700 text-xs">VEGA</div>
          <div className="text-xs text-emerald-500 leading-tight">Proactif</div>
        </div>
      </div>
    </div>

    {/* Légende routing */}
    <div className="mt-5 pt-4 border-t border-gray-200">
      <div className="text-xs text-gray-400 font-medium mb-2">Règles de routing (Phase 1)</div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-100">Questions app → Cygnus</span>
        <span className="px-2 py-1 bg-teal-50 text-teal-600 rounded-lg border border-teal-100">Keywords RH (admin) → Orion</span>
        <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded-lg border border-amber-100">Keywords HACCP/protocoles → Lyra</span>
        <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">Alertes, risques, état → VEGA</span>
      </div>
    </div>
  </div>
);

// ─── Composant carte agent actuel ──────────────────────────────────────────────

const AgentCard = ({ agent }) => {
  const [expanded, setExpanded] = useState(false);
  const c = COLOR_MAP[agent.color];
  const done = agent.features.filter(f => f.status === 'done').length;

  return (
    <div className={`rounded-xl border-2 ${c.card} p-5 flex flex-col gap-4`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-white ring-2 ${c.ring} flex items-center justify-center text-xl shadow-sm`}>
            {agent.emoji}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900 text-base">{agent.name}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.badge}`}>{agent.tagline}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{agent.description}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-gray-600">
          <Globe size={12} className="text-gray-400" />
          <span>{agent.access}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Layers size={12} className="text-gray-400" />
          <span>{agent.fab}</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">Fonctionnalités</span>
          <span className="text-xs font-medium text-gray-700">{done}/{agent.features.length}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full ${c.dot}`}
            style={{ width: `${(done / agent.features.length) * 100}%` }}
          />
        </div>
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition w-fit"
      >
        <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        {expanded ? 'Masquer le détail' : 'Voir le détail'}
      </button>

      {expanded && (
        <div className="space-y-1.5">
          {agent.features.map((f, i) => {
            const s = STATUS_BADGE[f.status];
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${s.cls}`}>
                  {s.label}
                </span>
                <span className={f.status === 'backlog' ? 'text-gray-400' : 'text-gray-700'}>{f.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Socle commun ──────────────────────────────────────────────────────────────

const SOCLE = [
  { icon: Zap,      label: 'llmService.js',    desc: '5 providers — Claude, OpenAI, Mistral, Gemini, Ollama' },
  { icon: FileText, label: '{agent}Rag.js',     desc: 'Chunking MD + PDF, recherche ILIKE, prêt pgvector' },
  { icon: Brain,    label: '{Agent}Memory',     desc: 'Mémoire persistante globale par agent' },
  { icon: Lock,     label: 'hrCrypto.js',       desc: 'Chiffrement AES-256-GCM des clés API en base' },
  { icon: Layers,   label: '{Agent}Settings',   desc: 'Config LLM + Personnalisation indépendante par agent' },
  { icon: Globe,    label: 'Context runtime',   desc: 'Module/onglet courant injecté dans chaque prompt' },
];

// ─── Composant principal ───────────────────────────────────────────────────────

const AgentEcosystem = () => (
  <div className="px-4 sm:px-6 lg:px-8 space-y-8 pb-8">

    {/* Header */}
    <div className="pt-2">
      <div className="flex items-center gap-2 mb-1">
        <Network size={20} className="text-purple-500" />
        <h2 className="text-lg font-bold text-gray-900">Écosystème agents IA</h2>
        <span className="text-xs bg-purple-100 text-purple-700 font-medium px-2 py-0.5 rounded-full">SuiviPlus v4.3.0</span>
      </div>
      <p className="text-sm text-gray-500">
        4 agents actifs coordonnés par Nova. Chaque agent a sa propre config LLM, base de connaissance et mémoire.
      </p>
    </div>

    {/* Schéma architecture */}
    <ArchitectureSchema />

    {/* Agents actifs */}
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Agents actifs</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {CURRENT_AGENTS.map(agent => <AgentCard key={agent.id} agent={agent} />)}
      </div>
    </div>

    {/* Flèche vers roadmap */}
    <div className="flex items-center gap-3">
      <div className="flex-1 border-t border-dashed border-gray-200" />
      <span className="text-xs text-gray-400 flex items-center gap-1.5 flex-shrink-0">
        <ChevronRight size={12} /> Feuille de route
      </span>
      <div className="flex-1 border-t border-dashed border-gray-200" />
    </div>

    {/* Roadmap */}
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Prochaines évolutions</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ROADMAP_AGENTS.map(agent => {
          const c = COLOR_MAP[agent.color];
          return (
            <div key={agent.id} className={`rounded-xl border-2 border-dashed ${c.card} p-4 opacity-80`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-white/60 ring-2 ring-dashed ring-gray-200 flex items-center justify-center text-lg">
                  {agent.emoji}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700 text-sm">{agent.name}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.badge}`}>{agent.tagline}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{agent.description}</p>
              <div className="text-xs text-gray-400 mt-2">{agent.eta}</div>
            </div>
          );
        })}
        {/* Carte Nova Phase 1 — déjà active */}
        <div className="rounded-xl border-2 border-purple-300 bg-purple-50/60 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-white ring-2 ring-purple-200 flex items-center justify-center text-lg shadow-sm">
              🔮
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700 text-sm">Nova Phase 1</span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Actif</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Orchestrateur rules-based actif dans le header. Détecte l'intention et route vers Cygnus, Orion, Lyra ou VEGA.
            Phase 2 (routing LLM Haiku) en backlog.
          </p>
        </div>
      </div>
    </div>

    {/* Socle commun */}
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Socle commun</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SOCLE.map(({ icon: Icon, label, desc }) => (
          <div key={label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <Icon size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-gray-700 font-mono">{label}</div>
              <div className="text-xs text-gray-400 mt-0.5 leading-relaxed">{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>

  </div>
);

export default AgentEcosystem;
